import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import tw from 'twin.macro';

import { Spacer } from '@components/Spacer';
import { Button } from '@components/Button';
import { Input } from '@auth_module/components/Input';
import { forgotPassword } from '@auth_module/services';

const StyledForm = tw.form`flex flex-col w-full gap-y-6`;
const StyledP = tw.p`text-[13px] p-0 text-text-and-icon`;
const ResetPasswordSentMessageWrapper = tw.div`flex flex-col items-center justify-center gap-y-3`;

type ForgotPasswordFormData = {
  email: string;
};

const ResetPasswordSentMessage = ({ email }: { email: string }) => {
  const { t } = useTranslation();
  return (
    <ResetPasswordSentMessageWrapper>
      <h3>{t('auth.forgot_password_form.reset_password_sent.title')}</h3>
      <StyledP>
        <Trans
          i18nKey='auth.forgot_password_form.reset_password_sent.description'
          values={{ email }}>
          If we find a user with
          <strong tw='text-accent-1'>email</strong>
          you will receive an email from us shortly.
        </Trans>
      </StyledP>
      <StyledP>
        <Trans i18nKey='auth.forgot_password_form.reset_password_sent.call_to_action'>
          If you don&apos;t receive an email in a few minutes, please check your spam folder or
          <a href='mailto:support@sounder.ai'>contact us</a> for further assistance.
        </Trans>
      </StyledP>
    </ResetPasswordSentMessageWrapper>
  );
};

const ForgotPasswordForm = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<ForgotPasswordFormData>({ email: '' });
  const [isResetPasswordSent, setIsResetPasswordSent] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [errorMessage, setErrorMessage] = useState<string | unknown>(null);

  const forgotPasswordSchema = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .required(t('auth.forgot_password_form.email.error_message_required') as string)
        .email(t('auth.forgot_password_form.email.error_message_invalid') as string),
    });
  }, [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({ resolver: yupResolver(forgotPasswordSchema) });

  const onSubmit: SubmitHandler<ForgotPasswordFormData> = async (formData) => {
    try {
      setFormData(formData);
      await forgotPassword(formData);
      setIsResetPasswordSent(true);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setErrorMessage(error?.message);
    }
  };

  return (
    <>
      {!isResetPasswordSent ? (
        <>
          <h3>{t('auth.forgot_password_form.title')}</h3>
          <Spacer direction='vertical' size='xxs' />

          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <StyledP>{t('auth.forgot_password_form.description')}</StyledP>
            <Input
              autoFocus
              tw='w-full'
              type='text'
              label={t('auth.forgot_password_form.email.label')}
              placeholder={t('auth.forgot_password_form.email.placeholder')}
              {...register('email')}
              error={errors.email?.message}
            />

            <Button.Primary type='submit' disabled={isSubmitting} isLoading={isSubmitting}>
              {t('auth.forgot_password_form.submit_button')}
            </Button.Primary>
          </StyledForm>
        </>
      ) : (
        <ResetPasswordSentMessage email={formData.email} />
      )}
    </>
  );
};

export { ForgotPasswordForm };
